import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Space, Spin, Typography, message } from 'antd';

import {
  EditorProvider,
  useEditorDoc,
  useEditorDispatch,
  useEditorMeta,
} from './EditorProvider';
import type { CatalogEntry, Diagnostic, EditorModule, NewPuzzleParams, ImportedState } from './types';
import { applyCreationMeta, type CreationMeta } from '../catalog/apply-creation-meta';

// O shell é dono do CICLO DE VIDA do documento; o módulo é dono do documento. Este
// componente é AGNÓSTICO de módulo — recebe um `EditorModule` e nunca aprende que
// cruzada tem grade. É o que o EditorShell.test prova com um módulo falso.

type AnyModule = EditorModule<unknown, unknown>;

/**
 * Metadados do "+ Novo jogo" (edição, tema, dificuldade) a gravar no registro que o
 * PRIMEIRO "Salvar" criar. `catalog` diz qual API chamar — vem do `entry` do módulo
 * no registry (o shell não lê o registry). Nada é gravado antes desse save.
 */
export interface CreationTarget {
  catalog: CatalogEntry['catalog'];
  meta: CreationMeta;
}

interface ShellProps {
  module: AnyModule;
  /** id a carregar; null = criação (usa `params`). */
  id: number | null;
  params: NewPuzzleParams | null;
  /** doc vindo do import; tem precedência sobre `params` na criação. */
  imported: ImportedState | null;
  /** Ausente/null = criação sem metadados (importação) ou edição de existente. */
  creation?: CreationTarget | null;
}

interface Loaded {
  doc: unknown;
  editavel: boolean;
}

export const EditorShell: React.FC<ShellProps> = ({ module, id, params, imported, creation = null }) => {
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'missing'>('loading');
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<ErrorInfo | null>(null);

  useEffect(() => {
    let alive = true;
    setState('loading');
    if (id != null) {
      module
        .load(id)
        .then((res) => {
          if (!alive) return;
          setLoaded({ doc: res.doc, editavel: res.editavel });
          setState('ready');
        })
        .catch((e) => {
          if (!alive) return;
          setLoadError({
            message: errMsg(e, 'Falha ao carregar o jogo.'),
            erros: errList(e),
          });
          setState('error');
        });
    } else if (imported) {
      setLoaded({ doc: imported.doc, editavel: true });
      setState('ready');
    } else if (params) {
      setLoaded({ doc: module.createEmpty(params), editavel: true });
      setState('ready');
    } else {
      setState('missing');
    }
    return () => {
      alive = false;
    };
  }, [module, id, params, imported]);

  // Nem todo módulo descobre o ranking no `load` (o show_wordsearch não devolve —
  // games-rec#39), então `editavel` pode nascer `true` num jogo que o servidor já
  // não deixa editar. Quando o save leva 409, o editor passa a somente leitura:
  // acende o banner (com "Duplicar para editar") e trava a grade. Sem isto o autor
  // volta para um editor que só sabe dar 409.
  const setReadOnly = useCallback(() => {
    setLoaded((prev) => (prev && prev.editavel ? { ...prev, editavel: false } : prev));
  }, []);

  if (state === 'loading') return <Spin tip="Carregando..." style={{ margin: 40 }} />;
  if (state === 'error')
    return (
      <Alert
        type="error"
        message={loadError?.message ?? 'Falha ao carregar o jogo.'}
        description={<ErrorList erros={loadError?.erros ?? []} />}
      />
    );
  if (state === 'missing')
    return <Alert type="info" message="Escolha um jogo para abrir ou crie um novo." />;
  if (!loaded) return null;

  return (
    <>
      {imported && imported.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Importado com avisos"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {imported.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          }
        />
      )}
      <EditorProvider
        key={id ?? 'new'}
        module={module}
        initialDoc={loaded.doc}
        editavel={loaded.editavel}
        initialUi={module.initialUi ? module.initialUi(loaded.doc) : {}}
        initiallyDirty={id == null && imported != null}
      >
        <EditorChrome module={module} id={id} creation={creation} onReadOnly={setReadOnly} />
      </EditorProvider>
    </>
  );
};

interface ErrorInfo {
  message: string;
  erros: string[];
}

const ErrorList: React.FC<{ erros: string[] }> = ({ erros }) =>
  erros.length === 0 ? null : (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {erros.map((erro, i) => (
        <li key={i}>{erro}</li>
      ))}
    </ul>
  );

function indicesEqual(a: Record<string, number>, b: Record<string, number>): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => a[k] === b[k]);
}

/**
 * A mensagem que o admin vê, na ordem em que ela é útil:
 * 1. o que o SERVIDOR disse (`response.data.msg`);
 * 2. a `message` de um `Error` embrulhado pelo módulo ou pelo serviço — é onde mora
 *    instrução acionável que o servidor não tem como dar (ex.: "o jogo #42 foi criado
 *    mas ficou sem grade, exclua-o", da criação em dois passos do caça-palavras).
 *    Sem este passo o embrulho era copy morta: nada aqui lia `e.message`;
 * 3. o texto genérico da tela.
 * Erro cru do axios não passa pelo passo 2 de propósito: "Request failed with status
 * code 500" não diz nada a quem está criando um passatempo.
 *
 * O passo 2 só confia num `Error` com `constructor === Error`: é a marca de um
 * embrulho DELIBERADO (todo módulo/serviço usa `new Error(...)` puro, nunca uma
 * subclasse). Um `TypeError`/`ReferenceError`/etc. de um bug de programação também é
 * `instanceof Error`, mas sua `.message` ("Cannot read properties of undefined
 * (reading 'game')") é texto de debug, não copy de UI — sem esta checagem ele
 * passava direto para a tela.
 */
function errMsg(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { msg?: unknown; message?: unknown } } })?.response
    ?.data;
  if (typeof data?.msg === 'string' && data.msg) return data.msg;
  // A API também responde `{ message, erros }` (422) e, em erros do Nest, `message` pode ser array.
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (data && Array.isArray(data.message)) {
    const joined = data.message.filter((m): m is string => typeof m === 'string').join(' ');
    if (joined) return joined;
  }
  const isAxiosError = (e as { isAxiosError?: boolean })?.isAxiosError === true;
  const isDeliberateWrap = e instanceof Error && e.constructor === Error;
  if (!isAxiosError && isDeliberateWrap && e.message) return e.message;
  return fallback;
}

/** Lista de problemas (`erros: string[]`) que o servidor anexa a um 422. */
function errList(e: unknown): string[] {
  const erros = (e as { response?: { data?: { erros?: unknown } } })?.response?.data?.erros;
  return Array.isArray(erros) ? erros.filter((x): x is string => typeof x === 'string') : [];
}

/** 409 = o servidor recusou a edição porque o passatempo já tem ranking. */
function isRankingConflict(e: unknown): boolean {
  return (e as { response?: { status?: number } })?.response?.status === 409;
}

const EditorChrome: React.FC<{
  module: AnyModule;
  id: number | null;
  creation: CreationTarget | null;
  /** Chamado quando o servidor recusa a edição (409): o shell vira somente leitura. */
  onReadOnly: () => void;
}> = ({ module, id, creation, onReadOnly }) => {
  const navigate = useNavigate();
  const doc = useEditorDoc<unknown>();
  const dispatch = useEditorDispatch();
  const { canUndo, canRedo, dirty, editavel } = useEditorMeta();

  const [saving, setSaving] = useState(false);
  const [drift, setDrift] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Erro do SAVE, à parte do `message.error` dos outros botões: o texto pode ser
  // longo e acionável (o embrulho do jogo órfão do caça-palavras passa de 400
  // caracteres) e os 3s de duração padrão do toast somem antes de alguém terminar de
  // ler. Um `Alert` fixo (só fecha com o X ou numa nova tentativa de salvar) resolve
  // a legibilidade sem precisar calcular duração por tamanho de texto.
  const [saveError, setSaveError] = useState<ErrorInfo | null>(null);
  // O meta da criação vale só para o save que CRIA o registro: uma vez aplicado (ou
  // tentado), saves seguintes nesta montagem não o repetem.
  const creationDone = useRef(false);

  // Guarda de saída suja: avisa antes de fechar/recarregar a aba com alterações.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const diagnostics: Diagnostic[] = module.validate(doc);
  const errors = diagnostics.filter((d) => d.level === 'error');

  const checkDrift = (serverIndices: Record<string, number>) => {
    if (!module.deriveIndices) return;
    setDrift(!indicesEqual(module.deriveIndices(doc), serverIndices));
  };

  const onPreview = async () => {
    try {
      const res = await module.preview?.(doc);
      if (!res) return;
      checkDrift(res.indices);
      message.success('Prévia gerada no servidor.');
    } catch (e) {
      message.error(errMsg(e, 'Falha na prévia.'));
    }
  };

  const onSave = async () => {
    if (errors.length > 0) {
      message.error('Corrija os erros antes de salvar.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const res = await module.save(id, doc);
      dispatch.markSaved();
      checkDrift(res.indices);
      message.success('Salvo.');
      if (id == null && creation && !creationDone.current) {
        creationDone.current = true;
        try {
          await applyCreationMeta(creation.catalog, res.id, creation.meta);
        } catch {
          // O registro já existe e fica: o meta é ajustável depois, o save não é desfeito.
          message.warning(
            'Salvo, mas edição, tema e dificuldade não foram gravados. Ajuste em Informações, na aba Jogos.',
            8,
          );
        }
      }
      // replace: o Voltar não pode reabrir o rascunho (params/imported) e duplicar o jogo.
      if (id == null) navigate(`/admin/editor/${module.kind}/${res.id}`, { replace: true });
    } catch (e) {
      // 409 = o servidor recusou porque o passatempo já tem ranking, e este é o
      // PRIMEIRO momento em que dá para saber (risco W2). Em vez de um modal — que o
      // autor fecha e volta a um editor que só sabe dar 409 —, o shell vira somente
      // leitura: o banner permanente acende com "Duplicar para editar" e a grade
      // trava. A mensagem de erro sai nos dois casos: falha de save sem rastro é o
      // autor achando que salvou.
      //
      // `id != null` é a guarda: na CRIAÇÃO não existe registro no servidor para
      // duplicar, então travar a grade só apagaria o trabalho que ainda é local.
      if (isRankingConflict(e) && id != null) {
        onReadOnly();
        setSaveError({
          message: errMsg(e, 'Este passatempo já tem ranking: o editor ficou em somente leitura.'),
          erros: errList(e),
        });
      } else {
        setSaveError({
          message: errMsg(e, 'Falha ao salvar.'),
          erros: errList(e),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const onDuplicate = async () => {
    if (id == null || !module.duplicate) return;
    setDuplicating(true);
    try {
      const res = await module.duplicate(id);
      message.success('Cópia criada. Abrindo para edição.');
      navigate(`/admin/editor/${module.kind}/${res.id}`);
    } catch (e) {
      message.error(errMsg(e, 'Falha ao duplicar.'));
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button onClick={dispatch.undo} disabled={!canUndo}>
          Desfazer
        </Button>
        <Button onClick={dispatch.redo} disabled={!canRedo}>
          Refazer
        </Button>
        {module.preview && (
          <Button onClick={onPreview} disabled={!editavel}>
            Prévia
          </Button>
        )}
        <Button
          type="primary"
          loading={saving}
          onClick={onSave}
          disabled={!editavel || errors.length > 0}
        >
          Salvar
        </Button>
        {dirty && <Typography.Text type="warning">alterações não salvas</Typography.Text>}
      </Space>

      {saveError && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message={saveError.message}
          description={<ErrorList erros={saveError.erros} />}
          onClose={() => setSaveError(null)}
        />
      )}

      {!editavel && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Somente leitura"
          description="Este passatempo já tem ranking — editar a grade trancaria quem já pontuou. Duplique para editar uma cópia."
          action={
            module.duplicate && id != null ? (
              <Button size="small" loading={duplicating} onClick={onDuplicate}>
                Duplicar para editar
              </Button>
            ) : undefined
          }
        />
      )}

      {drift && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="A numeração do editor divergiu da do servidor. Confira a grade — o servidor é a fonte da verdade."
        />
      )}

      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <module.Canvas />
        </div>
        <module.Inspector />
      </div>

      {diagnostics.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Typography.Title level={5}>Diagnósticos</Typography.Title>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {diagnostics.map((d, i) => (
              <Alert
                key={i}
                type={d.level === 'error' ? 'error' : 'warning'}
                message={d.message}
                showIcon
              />
            ))}
          </Space>
        </div>
      )}
    </div>
  );
};
